import DistanceFromStartIcon from '@assets/player/icons/distance_from_start.svg';
import DistanceGapIcon from '@assets/player/icons/distance_gap.svg';
import DistanceToFinishIcon from '@assets/player/icons/distance_to_finish.svg';
import AscentIcon from '@assets/player/icons/elevation_ascent.svg';
import DescentIcon from '@assets/player/icons/elevation_descent.svg';
import HandleIcon from '@assets/player/icons/handle.svg';
import VelocityIcon from '@assets/player/icons/velocity.svg';
import { velocityUnitByUnitType } from '@racemap/utilities/functions/units';
import { isDefined } from '@racemap/utilities/functions/validation';
import type { UnitType } from '@racemap/utilities/types/units';
import { useDrag } from '@use-gesture/react';
import classnames from 'classnames';
import { clamp } from 'lodash';
import { useCallback, useRef } from 'react';
import { animated, useSprings } from 'react-spring';
import { SHADOW_TRACK_STEP } from 'src/lib/player/constants';
import { t } from 'src/lib/player/i18n';
import {
  type PoiInfo,
  type StarterInfo,
  getPoiInfo,
  getStarterInfo,
} from 'src/lib/player/statistics';
import { type PoiType, type StarterType, useStore } from 'src/lib/player/store';
import {
  arrayMove,
  formatDistance,
  formatElevation,
  formatSpeed,
  formatTimeDuration,
} from 'src/lib/player/utils';
import type TrackView from 'src/lib/player/utils/TrackView';
import { binarySearchIndexLo } from 'src/lib/player/utils/binarySearch';
import { onClickGenerator } from 'src/lib/player/utils/onClickGenerator';
import { PoiIcon } from '../SideBar/PoiListItem';

const ROW_HEIGHT = 80;

function StarterNameCell({ starter }: { starter: StarterType }) {
  const numberLabel = starter.startNumber != null ? `${starter.startNumber}.` : '';
  return (
    <div className="starter-info-name-cell">
      <bdo>
        <span className="starter-startnumber">{numberLabel}</span>&nbsp;
        <span className="starter-name">{starter.name}</span>
      </bdo>
    </div>
  );
}
function PoiNameCell({ poi }: { poi: PoiType }) {
  return (
    <div className="starter-info-name-cell">
      <bdo>
        <PoiIcon poi={poi} />
        &nbsp;<span className="starter-name">{poi.name}</span>
      </bdo>
    </div>
  );
}

function SpeedCell({
  starterInfo,
  unitType,
}: {
  starterInfo: StarterInfo;
  unitType: UnitType;
}) {
  if (Number.isNaN(starterInfo.speed)) {
    return (
      <div className="starter-info-speed-cell" title={t('stats.currentSpeed')}>
        <img src={VelocityIcon} alt={t('stats.currentSpeed')} style={{ width: 10, height: 10 }} />
        &nbsp;
        <div>
          <span className="starter-value">-</span>
          <span className="starter-desc">{velocityUnitByUnitType(unitType)}</span>
        </div>
      </div>
    );
  }
  return (
    <div className="starter-info-speed-cell" title={t('stats.currentSpeed')}>
      <img src={VelocityIcon} alt={t('stats.currentSpeed')} style={{ width: 10, height: 10 }} />
      &nbsp;
      <div>
        <span className="starter-value">{formatSpeed(starterInfo.speed, unitType)}</span>
        <span className="starter-desc">{velocityUnitByUnitType(unitType)}</span>
      </div>
    </div>
  );
}

function DistanceCell({
  time,
  starterInfo,
  unitType,
}: {
  time: number;
  starterInfo: StarterInfo;
  unitType: UnitType;
}) {
  if (Number.isNaN(starterInfo.offset) || starterInfo.points.length < 1) {
    return (
      <div className="starter-info-distance-cell" title={t('stats.distanceFromStart')}>
        <img
          src={DistanceFromStartIcon}
          alt={t('stats.distanceFromStart')}
          style={{ width: 10, height: 10 }}
        />
        &nbsp;<span className="starter-value">-</span>
      </div>
    );
  }
  const offset = Math.abs(starterInfo.offset);
  const distanceLabel = formatDistance(offset, unitType);
  const pointIdx = binarySearchIndexLo(starterInfo.points, (points, i) => time - points[i].time);
  const timeLabel = formatTimeDuration(
    pointIdx >= 0 && pointIdx < starterInfo.points.length
      ? starterInfo.points[pointIdx].time - starterInfo.points[0].time
      : Number.NaN,
    false,
  );
  return (
    <div className="starter-info-distance-cell" title={t('stats.distanceFromStart')}>
      <img
        src={DistanceFromStartIcon}
        alt={t('stats.distanceFromStart')}
        style={{ width: 10, height: 10 }}
      />
      &nbsp;
      <div>
        <span className="starter-desc">{distanceLabel}</span>
        <span className="starter-desc">{timeLabel}</span>
      </div>
    </div>
  );
}

function PoiDistanceCell({
  poiInfo,
  unitType,
}: {
  poiInfo: PoiInfo;
  unitType: UnitType;
}) {
  if (Number.isNaN(poiInfo.offset)) {
    return (
      <div className="starter-info-distance-cell" title={t('stats.distanceFromStart')}>
        <img
          src={DistanceFromStartIcon}
          alt={t('stats.distanceFromStart')}
          style={{ width: 10, height: 10 }}
        />
        &nbsp;<span className="starter-value">-</span>
      </div>
    );
  }
  const offset = Math.abs(poiInfo.offset);
  const distanceLabel = formatDistance(offset, unitType);
  return (
    <div className="starter-info-distance-cell" title={t('stats.distanceFromStart')}>
      <img
        src={DistanceFromStartIcon}
        alt={t('stats.distanceFromStart')}
        style={{ width: 10, height: 10 }}
      />
      &nbsp;
      <div>
        <span className="starter-desc">{distanceLabel}</span>
      </div>
    </div>
  );
}

function TargetCell({
  starterInfo,
  unitType,
  shadowTrack,
}: {
  starterInfo: StarterInfo;
  unitType: UnitType;
  shadowTrack: TrackView;
}) {
  if (Number.isNaN(starterInfo.offset)) {
    return (
      <div className="starter-info-target-cell" title={t('stats.distanceToFinish')}>
        <img
          src={DistanceToFinishIcon}
          alt={t('stats.distanceToFinish')}
          style={{ width: 10, height: 10 }}
        />
        &nbsp;<span className="starter-value">-</span>
      </div>
    );
  }
  const shadowTrackLength = shadowTrack.size() * SHADOW_TRACK_STEP;
  const offset = Math.abs(shadowTrackLength - starterInfo.offset);
  const distanceLabel = formatDistance(offset, unitType);
  const timeLabel = formatTimeDuration(1000 * (offset / starterInfo.speed100), false);
  return (
    <div className="starter-info-target-cell" title={t('stats.distanceToFinish')}>
      <img
        src={DistanceToFinishIcon}
        alt={t('stats.distanceToFinish')}
        style={{ width: 10, height: 10 }}
      />
      &nbsp;
      <div>
        <span className="starter-desc">{distanceLabel}</span>
        <span className="starter-desc">{timeLabel}</span>
      </div>
    </div>
  );
}

function PoiTargetCell({
  poiInfo,
  unitType,
  shadowTrack,
}: {
  poiInfo: PoiInfo;
  unitType: UnitType;
  shadowTrack: TrackView;
}) {
  if (Number.isNaN(poiInfo.offset)) {
    return (
      <div className="starter-info-target-cell" title={t('stats.distanceToFinish')}>
        <img
          src={DistanceToFinishIcon}
          alt={t('stats.distanceToFinish')}
          style={{ width: 10, height: 10 }}
        />
        &nbsp;<span className="starter-value">-</span>
      </div>
    );
  }
  const shadowTrackLength = shadowTrack.size() * SHADOW_TRACK_STEP;
  const offset = Math.abs(shadowTrackLength - poiInfo.offset);
  const distanceLabel = formatDistance(offset, unitType);
  return (
    <div className="starter-info-target-cell" title={t('stats.distanceToFinish')}>
      <img
        src={DistanceToFinishIcon}
        alt={t('stats.distanceToFinish')}
        style={{ width: 10, height: 10 }}
      />
      &nbsp;
      <div>
        <span className="starter-desc">{distanceLabel}</span>
      </div>
    </div>
  );
}

function AscentCell({
  starterInfo,
  unitType,
}: {
  starterInfo: StarterInfo;
  unitType: UnitType;
}) {
  if (Number.isNaN(starterInfo.ascent) || Number.isNaN(starterInfo.descent)) {
    return (
      <div className="starter-info-ascent-cell" title={t('stats.accumulatedAscentDescent')}>
        <span className="starter-desc">
          <img src={AscentIcon} alt="Ascent" style={{ width: 10, height: 10 }} />
          &nbsp;-
        </span>
        <span className="starter-desc">
          <img src={DescentIcon} alt="Descent" style={{ width: 10, height: 10 }} />
          &nbsp;-
        </span>
      </div>
    );
  }

  const ascentLabel = formatElevation(starterInfo.ascent, unitType);
  const descentLabel = formatElevation(starterInfo.descent, unitType);
  return (
    <div className="starter-info-ascent-cell" title={t('stats.accumulatedAscentDescent')}>
      <span className="starter-desc">
        <img src={AscentIcon} alt="Ascent" style={{ width: 10, height: 10 }} />
        &nbsp;{ascentLabel}
      </span>
      <span className="starter-desc">
        <img src={DescentIcon} alt="Descent" style={{ width: 10, height: 10 }} />
        &nbsp;{descentLabel}
      </span>
    </div>
  );
}

function GapCell({
  starterInfo,
  followedInfo,
  unitType,
  shadowTrack,
}: {
  starterInfo: StarterInfo | PoiInfo;
  followedInfo: StarterInfo | PoiInfo;
  unitType: UnitType;
  shadowTrack: TrackView | null;
}) {
  if (shadowTrack == null || starterInfo.id === followedInfo.id) {
    return <div className="starter-info-gap-cell" />;
  }
  const distance =
    starterInfo.offset >= 0 && followedInfo.offset >= 0
      ? starterInfo.offset - followedInfo.offset
      : Number.NaN;
  const deltaTime =
    starterInfo.type === 'STARTER' &&
    starterInfo.speed !== 0 &&
    !Number.isNaN(starterInfo.speed) &&
    !Number.isNaN(distance)
      ? 1000 * (distance / starterInfo.speed)
      : Number.NaN;
  const followedName =
    followedInfo.type === 'STARTER' ? (
      followedInfo.starter.startNumber
    ) : (
      <PoiIcon poi={followedInfo.poi} scale={0.15} />
    );
  return (
    <div className="starter-info-gap-cell">
      {t('stats.gap')}&nbsp;{followedName}:&nbsp;&nbsp;
      {Number.isNaN(distance) ? (
        '-'
      ) : (
        <>
          <span>
            <bdo>
              <img src={DistanceGapIcon} alt="Distance Gap" style={{ width: 10, height: 10 }} />
              &nbsp;{formatDistance(distance, unitType)}
            </bdo>
          </span>
          &nbsp;&nbsp;
          {Number.isNaN(deltaTime) ? null : (
            <span>
              <bdo>{formatTimeDuration(deltaTime, true)}</bdo>
            </span>
          )}
        </>
      )}
    </div>
  );
}

function DraggableList<T>({
  items,
  onRender,
  onReorder,
}: {
  items: Array<T>;
  onRender: (item: T) => JSX.Element;
  onReorder: (items: Array<T>) => void;
}) {
  const order = useRef(items.map((_, i) => i));
  const [springs, setSprings] = useSprings(items.length, (index: number) => ({
    y: order.current.indexOf(index) * ROW_HEIGHT,
    zIndex: 0,
    immediate: false,
  }));
  const bind = useDrag(({ args: [originalIndex], down, last, movement: [, y], event }) => {
    event.preventDefault();
    const curIndex = order.current.indexOf(originalIndex);
    const curRow = clamp(Math.round((curIndex * ROW_HEIGHT + y) / ROW_HEIGHT), 0, items.length - 1);
    const newOrder = arrayMove(order.current, curIndex, curRow);
    if (down) {
      setSprings((index: number) =>
        index === originalIndex
          ? {
              y: curIndex * ROW_HEIGHT + y,
              zIndex: 1,
              immediate: !last,
            }
          : {
              y: newOrder.indexOf(index) * ROW_HEIGHT,
              zIndex: 0,
              immediate: false,
            },
      );
    } else {
      setSprings((index: number) => ({
        y: newOrder.indexOf(index) * ROW_HEIGHT,
        zIndex: 0,
        immediate: false,
      }));
      order.current = newOrder;
    }
    if (last && order.current.some((a, i) => a !== i)) {
      setTimeout(() => onReorder(order.current.map((i) => items[i])), 300);
    }
  }, {});
  return (
    <div className="starter-info-content" style={{ height: items.length * ROW_HEIGHT }}>
      {springs.map(({ zIndex, y }, i) => {
        return (
          <animated.div
            // biome-ignore lint/suspicious/noArrayIndexKey: fine to use the index as key
            key={i}
            className={classnames('starter-info-row', {
              dragging: zIndex.to((v) => v === 1).get(),
              odd: y
                .to((v) => clamp(Math.round(v / ROW_HEIGHT), 0, items.length - 1) % 2 === 1)
                .get(),
            })}
            style={{
              height: ROW_HEIGHT - 10,
              transform: y.interpolate((y) => `translate3d(0,${y}px,0)`),
            }}
          >
            <div className="starter-info-row-handle" {...bind(i)}>
              <img src={HandleIcon} alt="" />
            </div>
            {onRender(items[i])}
          </animated.div>
        );
      })}
    </div>
  );
}

export default function StarterInfoTable(): JSX.Element {
  const setSelected = useStore((state) => state.actions.setSelected);
  const toggleSelectedStarter = useStore((state) => state.actions.toggleSelectedStarter);
  const time = useStore((state) => state.timer.time);
  const unitType = useStore((state) => state.options.unitType);
  const shadowTrack = useStore(
    (state) => state.events[state.currentEventIndices[0]].chunkedShadowTrack,
  );
  const selectedInfos: Array<PoiInfo | StarterInfo> = useStore((state) =>
    state.timer.selected
      .map((id) => {
        const currentEventIndex = state.currentEventIndices[0];
        const poi = state.events[currentEventIndex].pois.find((poi) => poi.id === id);
        if (poi != null) {
          return getPoiInfo(state, poi);
        }
        const starter = (state.startersById.get(id) ?? []).find(
          (starter) => starter.eventIndex === currentEventIndex,
        );
        if (starter != null) {
          return getStarterInfo(state, starter);
        }
        return null;
      })
      .filter(isDefined),
  );

  const followedInfo = selectedInfos[0];

  const handleReorder = useCallback(
    function handleReorder(ids: Array<string>) {
      setSelected(ids);
    },
    [setSelected],
  );

  return (
    <div className="starter-info-table">
      <DraggableList
        key={selectedInfos.map((item) => item.id).join(',')}
        onRender={(selectedInfo) => (
          <>
            {selectedInfo.type === 'STARTER' ? (
              <div key={selectedInfo.starter.id} className="starter-info-row-content">
                <StarterNameCell starter={selectedInfo.starter} />
                <div className="starter-info-stats-cell">
                  <SpeedCell starterInfo={selectedInfo} unitType={unitType} />
                  <DistanceCell time={time} starterInfo={selectedInfo} unitType={unitType} />
                  <AscentCell starterInfo={selectedInfo} unitType={unitType} />
                  {shadowTrack != null && (
                    <TargetCell
                      shadowTrack={shadowTrack}
                      starterInfo={selectedInfo}
                      unitType={unitType}
                    />
                  )}
                </div>
                <GapCell
                  starterInfo={selectedInfo}
                  followedInfo={followedInfo}
                  unitType={unitType}
                  shadowTrack={shadowTrack}
                />
              </div>
            ) : (
              <div key={selectedInfo.poi.id} className="starter-info-row-content">
                <PoiNameCell poi={selectedInfo.poi} />
                <div className="starter-info-stats-cell">
                  <div className="starter-info-speed-cell" />
                  <PoiDistanceCell poiInfo={selectedInfo} unitType={unitType} />
                  <div className="starter-info-ascent-cell" />
                  {shadowTrack != null && (
                    <PoiTargetCell
                      shadowTrack={shadowTrack}
                      poiInfo={selectedInfo}
                      unitType={unitType}
                    />
                  )}
                </div>
                <GapCell
                  starterInfo={selectedInfo}
                  followedInfo={followedInfo}
                  unitType={unitType}
                  shadowTrack={shadowTrack}
                />
              </div>
            )}
            <div className="starter-info-button-cell">
              <div
                className="button"
                {...onClickGenerator(() => toggleSelectedStarter(selectedInfo.id))}
              >
                <i className="fas fa-times" />
              </div>
            </div>
          </>
        )}
        items={selectedInfos}
        onReorder={(newItems) => {
          if (newItems.length > 0) {
            handleReorder(newItems.map((info) => info.id));
          }
        }}
      />
    </div>
  );
}
